/**
 * Central Component Registry
 * Collects ALL pluggable components at build time with runtime discovery support
 */

import React from 'react';
import type { UIComponentContract } from '../types/componentContract';

// Component entry type
export interface ComponentEntry {
	contract: UIComponentContract;
	component: React.ComponentType<any>;
	capabilityIndex: string;
	renderIndex: string;
}

// Build-time component collection
// Components will be imported here as they're created
import ProfileCard from '../components/specDriven/ProfileCard';
import AnalyticsSummary from '../components/specDriven/AnalyticsSummary';
import PostComposer from '../components/specDriven/PostComposer';
import ItemsList from '../components/specDriven/ItemsList';
import ItemsTable from '../components/specDriven/ItemsTable';
// Import chart and table components
import StatCard from '../components/specDriven/StatCard';
import BarChart from '../components/specDriven/BarChart';
import DonutChart from '../components/specDriven/DonutChart';
import DataTable from '../components/specDriven/DataTable';
// Import existing connector components
import LinkedInOptions from '../navigation/screens/MidLevel/IntegrationsScreen/components/subcomponents/integrationOptions/LinkedInOptions';
// import IntegrationComponent from '../navigation/screens/MidLevel/IntegrationsScreen/components/IntegrationComponent';
// Add more imports as components are created

// Build-time collection
export const UI_COMPONENTS = [
	ProfileCard,
	AnalyticsSummary,
	PostComposer,
	ItemsList,
	ItemsTable,
	StatCard,
	BarChart,
	DonutChart,
	DataTable,
	LinkedInOptions,
	// IntegrationComponent,
	// Add more components here as they're created
].filter((c) => c && (typeof c === 'function' || typeof c === 'object'));

// Indexed registry for fast lookup
export const COMPONENT_INDEX: ComponentEntry[] = UI_COMPONENTS.filter(
	(c: any) => c.uiContract
) // Only include components with contracts
	.map((c: any) => ({
		contract: c.uiContract,
		component: c,
		// Pre-computed indexes for faster resolution
		capabilityIndex: c.uiContract.accepts.capability,
		renderIndex: c.uiContract.renders,
	}));

// Capability-based index for O(1) lookup
export const CAPABILITY_INDEX = new Map<string, ComponentEntry[]>();
COMPONENT_INDEX.forEach((entry) => {
	const capability = entry.contract.accepts.capability;
	if (!CAPABILITY_INDEX.has(capability)) {
		CAPABILITY_INDEX.set(capability, []);
	}
	CAPABILITY_INDEX.get(capability)!.push(entry);
});

// Render type index
export const RENDER_INDEX = new Map<string, ComponentEntry[]>();
COMPONENT_INDEX.forEach((entry) => {
	const renderType = entry.contract.renders;
	if (!RENDER_INDEX.has(renderType)) {
		RENDER_INDEX.set(renderType, []);
	}
	RENDER_INDEX.get(renderType)!.push(entry);
});

// Runtime component registration (for hot-reload, testing, or dynamic additions)
const runtimeComponents: ComponentEntry[] = [];

/**
 * Register a component at runtime
 * Useful for new connectors without rebuild
 */
export function registerRuntimeComponent(component: React.ComponentType<any>) {
	if ((component as any).uiContract) {
		const entry: ComponentEntry = {
			contract: (component as any).uiContract,
			component,
			capabilityIndex: (component as any).uiContract.accepts.capability,
			renderIndex: (component as any).uiContract.renders,
		};
		runtimeComponents.push(entry);

		// Update indexes
		const capability = entry.contract.accepts.capability;
		if (!CAPABILITY_INDEX.has(capability)) {
			CAPABILITY_INDEX.set(capability, []);
		}
		CAPABILITY_INDEX.get(capability)!.push(entry);

		const renderType = entry.contract.renders;
		if (!RENDER_INDEX.has(renderType)) {
			RENDER_INDEX.set(renderType, []);
		}
		RENDER_INDEX.get(renderType)!.push(entry);
	}
}

/**
 * Get all components (build-time + runtime)
 */
export function getAllComponents(): ComponentEntry[] {
	return [...COMPONENT_INDEX, ...runtimeComponents];
}

/**
 * Get components by render type (e.g., "chart", "table", "card")
 */
export function getComponentsByType(renderType: string): ComponentEntry[] {
	return RENDER_INDEX.get(renderType) || [];
}

/**
 * Get component by ID (e.g., "chart.bar", "table.data")
 */
export function getComponentById(componentId: string): ComponentEntry | null {
	const allComponents = getAllComponents();
	return allComponents.find(entry => entry.contract.id === componentId) || null;
}

/**
 * Get components by capability (e.g., "display_chart", "display_table")
 */
export function getComponentsByCapability(capability: string): ComponentEntry[] {
	return CAPABILITY_INDEX.get(capability) || [];
}
